import classNames from 'classnames';
import type { LngLat } from 'mapbox-gl';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import IconButton from '@alltrails/denali/components/IconButton';
import Close from '@alltrails/denali/icons/Close';
import PaddingAwarePopup from '@alltrails/maps/components/PaddingAwarePopup/PaddingAwarePopup';
import { PublicLandFeatureProperties } from '../../types/PublicLandFeatures';
import { formatPropertyValue, propertyLabels, publicLandsPopupOffset } from '../../utils/constants';
import styles from './PublicLandsPopup.module.scss';

type PublicLandsPopupProps = {
  selectedFeature: { properties: PublicLandFeatureProperties | undefined; lngLat: LngLat } | null;
  handleClosePopup: () => void;
  popupFeature: { properties: PublicLandFeatureProperties | undefined; lngLat: LngLat };
};

const PublicLandsPopup = ({ selectedFeature, handleClosePopup, popupFeature }: PublicLandsPopupProps) => {
  const intl = useIntl();

  return (
    <PaddingAwarePopup
      maxWidth="none"
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      latitude={popupFeature.lngLat.lat}
      longitude={popupFeature.lngLat.lng}
      offset={publicLandsPopupOffset}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <Typography component="div" variant="text300bold" className={styles.title}>
            {popupFeature.properties?.unit_nm || intl.formatMessage({ defaultMessage: 'U.S. public lands' })}
          </Typography>
          <IconButton
            title={intl.formatMessage({ defaultMessage: 'Close' })}
            className={classNames(styles.closeButton, { [styles.isVisible]: !!selectedFeature })}
            icon={{ Component: Close }}
            variant="flat"
            size="sm"
            onClick={handleClosePopup}
          />
        </div>
        <div className={styles.properties}>
          {['mang_name', 'des_tp', 'pub_access'].map(key => {
            const value = popupFeature.properties?.[key] ?? null;
            if (value === null || value === undefined) return null;
            return (
              <div key={key} className={styles.property}>
                <Typography variant="text100" className={styles.propertyKey}>
                  {propertyLabels[key]}:
                </Typography>
                <Typography variant="text100" className={styles.propertyValue}>
                  {formatPropertyValue(key, value)}
                </Typography>
              </div>
            );
          })}
        </div>
      </div>
    </PaddingAwarePopup>
  );
};

export default PublicLandsPopup;
